import { ArrowRight, CheckCircle2, Search, Sparkles } from '../components/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { crmService } from '../services/crmService';
import { useAuth } from '../store/authContext';
import { useToast } from '../store/toastContext';

const EMPTY_QUERY_EXAMPLE = 'Busque por empresa de manuntenção em, campinas(sp)';
const LOADING_STEPS = [
  'Conectando ao Google e mapeando empresas do nicho...',
  'Validando localização, contatos e sinais de atividade...',
  'Filtrando empresas com maior aderência ao perfil ideal...',
  'Preparando os resultados para envio ao Kanban...'
];
const GOOGLE_WEBHOOK_BASE = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/bcc64ed6-84a6-4d45-beb2-cbda23c9783e/webhook-test';
const EXTENSION_ZIP_URL = '/EXTENSAO_APP.zip';

export default function ExtracaoGooglePage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingQuery, setLoadingQuery] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const { session } = useAuth();

  useEffect(() => {
    let active = true;
    const loadDefaultQuery = async () => {
      setLoadingQuery(true);
      const remoteQuery = await crmService.getGoogleSearchQuery();
      if (!active) return;
      setQuery(remoteQuery);
      setLoadingQuery(false);
    };
    loadDefaultQuery();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      return;
    }

    const timer = window.setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 1400);

    return () => window.clearInterval(timer);
  }, [loading]);

  const runSearch = async () => {
    const sanitizedQuery = query.trim();
    if (sanitizedQuery.length < 3) {
      pushToast({ type: 'error', title: 'Digite ao menos 3 caracteres para pesquisar.' });
      return;
    }
    setLoading(true);
    try {
      await crmService.saveGoogleSearchQuery(sanitizedQuery, '');
      const generated = await crmService.runGoogleSearch(sanitizedQuery);
      pushToast({ type: 'success', title: `${generated.length} empresas captadas e enviadas para o Kanban.` });
      navigate('/kanban');
    } catch {
      pushToast({ type: 'error', title: 'Não foi possível salvar a busca digitada.' });
    } finally {
      setLoading(false);
    }
  };

  const sessionToken = session?.token?.trim() || ':token_sessao';
  const webhookUrl = `${GOOGLE_WEBHOOK_BASE}/${encodeURIComponent(sessionToken)}`;

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      pushToast({ type: 'success', title: 'Webhook copiado com sucesso.' });
    } catch {
      pushToast({ type: 'error', title: 'Nao foi possivel copiar o webhook.' });
    }
  };

  const downloadExtensionZip = () => {
    window.open(EXTENSION_ZIP_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Extração</p>
        <h1 className="text-2xl font-black">Google Leads</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">Busque segmentos, cidades ou nichos e envie os resultados para o Kanban.</p>
      </div>

      <Card className="space-y-4">
        <Input
          label="Consulta no Google"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading}
          placeholder={EMPTY_QUERY_EXAMPLE}
          iconLeft={<Search className="h-4 w-4" />}
        />
        {!loadingQuery && !query.trim() ? (
          <p className="text-xs text-slate-500 dark:text-slate-300">
            Nenhuma busca cadastrada no momento. Exemplo: <span className="font-semibold">{EMPTY_QUERY_EXAMPLE}</span>
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="cta" onClick={runSearch} disabled={loading || loadingQuery}>
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 animate-spin" />
                Buscando...
              </span>
            ) : loadingQuery ? (
              'Carregando busca...'
            ) : (
              'Buscar empresas'
            )}
          </Button>
        </div>
      </Card>

      <Card className="space-y-3">
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-slate-700 dark:border-cta/30 dark:bg-cta/10 dark:text-slate-200">
          <p className="font-semibold">Como usar na extensao gratuita</p>
          <p className="mt-1">
            Copie o webhook abaixo e cole na configuracao da extensao de extracao. Depois baixe o pacote da extensao, descompacte e carregue no Opera.
          </p>
          <p className="mt-1 text-[11px] opacity-80">
            Nota: o Opera nao instala extensao a partir de arquivo zip criptografado. O fluxo suportado e baixar, extrair e carregar a pasta.
          </p>
        </div>
        <Input label="Webhook por usuario (sessao)" value={webhookUrl} readOnly />
        <Input label="Pacote da extensao (.zip)" value={EXTENSION_ZIP_URL} readOnly />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyWebhook} iconLeft={<CheckCircle2 />}>
            Copiar webhook
          </Button>
          <Button variant="outline" onClick={downloadExtensionZip} iconLeft={<ArrowRight />}>
            Baixar extensao (.zip)
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card className="google-search-loading-card space-y-5 overflow-hidden">
          <div className="google-search-grid" />
          <div className="google-search-scanline" />
          <div className="google-search-progress" />
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="google-search-scene">
              <div className="google-search-beam" />
              <div className="google-search-ring google-search-ring-a" />
              <div className="google-search-ring google-search-ring-b" />
              <div className="google-search-orbit google-search-orbit-inner" />
              <div className="google-search-orbit google-search-orbit-outer" />
              <div className="google-search-core">
                <Sparkles className="h-5 w-5" />
              </div>
            </div>
            <div>
              <p className="google-search-title text-sm font-bold text-slate-800 dark:text-slate-100">Buscando empresas no Google</p>
              <p className="text-xs text-slate-600 dark:text-slate-300">{LOADING_STEPS[loadingStep]}</p>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {LOADING_STEPS.map((step, index) => (
              <div
                key={step}
                className={`google-search-step rounded-xl border px-3 py-2 text-xs transition-all duration-300 ${
                  index === loadingStep
                    ? 'google-search-step-active border-primary/50 bg-primary/10 text-slate-900 dark:border-cta/50 dark:bg-cta/15 dark:text-slate-100'
                    : 'border-borderLight/70 bg-white/40 text-slate-500 dark:border-borderDark/70 dark:bg-slate-900/20 dark:text-slate-400'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="rounded-xl border border-dashed border-borderLight p-8 text-center dark:border-borderDark">
          <Sparkles className="mx-auto h-5 w-5 text-slate-400" />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-300">
            Ao concluir a busca, o sistema cria empresas mockadas e abre o Kanban automaticamente.
          </p>
        </div>
      </Card>
    </div>
  );
}
